import React, { useState } from 'react';
import SEO from '../components/SEO';

interface Metric {
  value: string;
  label: string;
}

interface CaseStudy {
  id: string;
  title: string;
  client: string;
  location: string;
  category: 'AI & Vision' | 'Automation' | 'Predictive Analytics';
  summary: string;
  challenge: string;
  solution: string;
  results: string[];
  techStack: string[];
  metrics: Metric[];
  imageBg: string; // Tailwind gradient classes
}

const CaseStudiesPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [activeCase, setActiveCase] = useState<CaseStudy | null>(null);

  const categories = ['All', 'AI & Vision', 'Automation', 'Predictive Analytics'];

  const caseStudies: CaseStudy[] = [
    {
      id: 'gulf-logistics',
      title: 'Automated Container Damage Inspection',
      client: 'Gulf Shipping Terminal Hub',
      location: 'Doha, Qatar',
      category: 'AI & Vision',
      summary: 'Automating structural damage checks of incoming shipping containers using high-speed gate cameras and custom computer vision models.',
      challenge: 'Manual inspections of shipping containers at terminal gates took 10-15 minutes per container, causing significant truck bottlenecks. Human error also led to missed structural defects, resulting in costly disputes and safety hazards.',
      solution: 'AlmeOne, in partnership with Sradha Technologies, deployed a smart gate computer vision system. As trucks pass the gate at 15 km/h, high-resolution cameras capture container surfaces. An AI model trained on custom datasets identifies dents, structural cracks, rust, and door latch anomalies in real-time.',
      results: [
        'Reduced gate inspection times from 12 minutes to under 3 seconds per container.',
        'Achieved a 99.2% accuracy rate in detecting structural damages.',
        'Eliminated terminal entry queues, boosting gate throughput by 300%.',
        'Reduced damage liability disputes by 85% due to automated photo logging.'
      ],
      techStack: ['PyTorch', 'OpenCV', 'TensorRT', 'CUDA', 'FastAPI', 'Docker'],
      metrics: [
        { value: '3s', label: 'Processing Time' },
        { value: '99.2%', label: 'AI Accuracy' },
        { value: '85%', label: 'Disputes Reduced' }
      ],
      imageBg: 'from-blue-600 to-indigo-900'
    },
    {
      id: 'smartstore-fulfillment',
      title: 'AI-Driven E-Commerce Demand Optimization',
      client: 'SmartStore Fulfillment Network',
      location: 'Doha, Qatar',
      category: 'Predictive Analytics',
      summary: 'Implementing automated demand forecasting models to optimize stock levels and reduce inventory holding costs.',
      challenge: 'Volatile consumer buying patterns led to frequent stockouts on fast-moving items and expensive overstocking on slow-moving inventory, raising warehouse storage costs by 24%.',
      solution: 'We developed an advanced predictive analytics engine that integrates historical order data, seasonal marketing spikes, local holidays, and weather patterns. The platform automatically adjusts inventory reorder thresholds dynamically for over 15,000 SKUs.',
      results: [
        'Decreased inventory carrying costs by 28% in the first two quarters.',
        'Reduced stockout incidents on high-demand products by 85%.',
        'Improved order forecasting accuracy to 95%.',
        'Minimized waste of perishable goods through automated age-tracking alerts.'
      ],
      techStack: ['Python', 'Pandas', 'Scikit-Learn', 'AWS Forecast', 'Node.js', 'PostgreSQL'],
      metrics: [
        { value: '28%', label: 'Lower Carrying Costs' },
        { value: '85%', label: 'Fewer Stockouts' },
        { value: '95%', label: 'Forecast Accuracy' }
      ],
      imageBg: 'from-amber-500 to-red-700'
    },
    {
      id: 'petrochemical-conveyor',
      title: 'Predictive IoT Maintenance for Distribution',
      client: 'Qatar Petrochemical & Logistics Corp',
      location: 'Mesaieed, Qatar',
      category: 'Automation',
      summary: 'Preventing unplanned conveyor breakdowns in high-throughput distribution lines using IoT vibration sensors and anomaly models.',
      challenge: 'Unexpected breakdowns of major distribution conveyor belts cost the client approximately $15,000 per hour in idle labor and delayed shipments, hindering global supply chains.',
      solution: 'We installed industrial IoT vibration and temperature sensors at critical motor and bearing joints. The real-time sensor streams are ingested by AlmeOne\'s edge analytics platform, running anomaly detection algorithms to identify signs of mechanical fatigue.',
      results: [
        'Reduced unexpected system downtime by 72%.',
        'Extended conveyor motor lifespans by an average of 20%.',
        'Shifted maintenance workflow from reactive troubleshooting to scheduled off-peak repairs.',
        'Provided 24/7 web dashboard alerting facilities teams before thermal failures.'
      ],
      techStack: ['IoT Edge', 'MQTT', 'InfluxDB', 'LSTM Networks', 'React', 'Grafana'],
      metrics: [
        { value: '72%', label: 'Downtime Reduced' },
        { value: '20%', label: 'Equipment Lifespan' },
        { value: '$120k+', label: 'Annual Savings' }
      ],
      imageBg: 'from-emerald-600 to-teal-900'
    },
    {
      id: 'express-dimensioning',
      title: 'Automated Cargo Dimensioning System',
      client: 'Al-Khaleej Express Cargo',
      location: 'Doha, Qatar',
      category: 'Automation',
      summary: 'Speeding up logistics billing by replacing manual box measuring tape dimensioning with instant 3D camera scans.',
      challenge: 'Manual measurement of cargo boxes using tape measures took warehouse workers up to 45 seconds per package, creating billing delays and leading to incorrect cargo volume estimations.',
      solution: 'Designed and deployed an overhead dimensioning rig equipped with 3D depth cameras and weight scales. As cargo is placed on the scale, the system measures length, width, height, and weight in 0.5 seconds, auto-syncing the data with their ERP system.',
      results: [
        'Accelerated packaging dimension measurement speed by 90 times.',
        'Achieved measurement precision within 5mm margin of error.',
        'Saved workers over 150 hours per month in manual data logging.',
        'Eliminated billing volume inaccuracies, recovering 8% in lost cargo revenue.'
      ],
      techStack: ['Intel RealSense SDK', 'C#', '.NET Core', 'WPF', 'MSSQL Server', 'TCP/IP Socket'],
      metrics: [
        { value: '90x', label: 'Faster Capture' },
        { value: '0.5s', label: 'Scan Time' },
        { value: '8%', label: 'Revenue Recovered' }
      ],
      imageBg: 'from-purple-600 to-indigo-950'
    }
  ];

  const filteredCases = selectedCategory === 'All'
    ? caseStudies
    : caseStudies.filter(c => c.category === selectedCategory);

  return (
    <div className="bg-gray-50 min-h-screen">
      <SEO 
        title="Case Studies - AlmeOne | Real-world AI & Automation Impact"
        description="Explore AlmeOne's case studies on container damage detection, predictive maintenance, and e-commerce inventory optimization in Qatar."
        keywords="case studies, AI success stories, logistics automation Qatar, container inspection AI, warehouse automation cases"
      />

      {/* Page Header */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-gold/20 py-20 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-gold/10 via-transparent to-transparent"></div>
        <div className="max-w-6xl mx-auto px-5 text-center relative z-10">
          <h1 className="text-5xl lg:text-6xl font-bold mb-6">
            Our <span className="text-gold">Case Studies</span>
          </h1>
          <p className="text-xl lg:text-2xl text-gray-300 max-w-3xl mx-auto">
            Discover how AlmeOne and Sradha Technologies deliver high-impact AI, IoT, and automated logistics solutions for market leaders.
          </p>
        </div>
      </section>

      {/* Filter Options */}
      <section className="py-8 bg-white border-b border-gray-100 sticky top-[72px] sm:top-[88px] z-20 shadow-sm">
        <div className="max-w-6xl mx-auto px-5 flex flex-wrap gap-2 justify-center">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-5 py-2.5 rounded-full font-semibold transition-all duration-300 text-sm border ${
                selectedCategory === cat
                  ? 'bg-gold text-black border-gold shadow-md'
                  : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-gold hover:text-gold'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Grid of Case Studies */}
      <section className="py-16 max-w-6xl mx-auto px-5">
        <div className="grid md:grid-cols-2 gap-8">
          {filteredCases.map((study) => (
            <div 
              key={study.id}
              className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl border border-gray-100 flex flex-col transition-all duration-500 transform hover:-translate-y-2 group"
            >
              {/* Card visual banner */}
              <div className={`h-48 bg-gradient-to-br ${study.imageBg} relative p-8 flex flex-col justify-end text-white overflow-hidden`}>
                <div className="absolute top-4 right-4 bg-white/15 backdrop-blur-md px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider text-gold">
                  {study.category}
                </div>
                <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-white/5 rounded-full group-hover:scale-125 transition-transform duration-700"></div>
                <p className="text-sm text-gold/90 font-medium mb-1">{study.client}</p>
                <h3 className="text-2xl font-bold leading-tight">{study.title}</h3>
              </div>

              {/* Card content */}
              <div className="p-8 flex-1 flex flex-col">
                <p className="text-gray-600 mb-8 text-sm sm:text-base flex-1">
                  {study.summary}
                </p>

                {/* Key Metrics */}
                <div className="grid grid-cols-3 gap-4 border-t border-b border-gray-100 py-4 mb-6 text-center">
                  {study.metrics.map((metric, i) => (
                    <div key={i}>
                      <div className="text-lg sm:text-xl font-extrabold text-gold">{metric.value}</div>
                      <div className="text-[10px] sm:text-xs text-gray-500 uppercase font-semibold mt-0.5">{metric.label}</div>
                    </div>
                  ))}
                </div>

                {/* Action button */}
                <button
                  onClick={() => setActiveCase(study)}
                  className="w-full text-center bg-gray-950 text-white font-bold py-3.5 rounded-xl hover:bg-gold hover:text-black transition-all duration-300 flex items-center justify-center gap-2"
                >
                  Read Case Study 
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Case Study Detail Modal Overlay */}
      {activeCase && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4 sm:p-6 lg:p-8 animate-fade-in">
          <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl relative border border-gray-100">
            
            {/* Sticky Header Banner */}
            <div className={`p-8 sm:p-10 bg-gradient-to-br ${activeCase.imageBg} text-white sticky top-0 z-10 flex flex-col justify-end`}>
              <button 
                onClick={() => setActiveCase(null)}
                className="absolute top-4 right-4 bg-black/40 hover:bg-black/60 hover:scale-105 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg transition-all"
                aria-label="Close Case Study"
              >
                ✕
              </button>
              <div className="bg-white/10 backdrop-blur-md px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider text-gold w-fit mb-3">
                {activeCase.category}
              </div>
              <p className="text-gold/90 text-sm font-semibold mb-1">{activeCase.client}</p>
              <h2 className="text-2xl sm:text-4xl font-extrabold leading-tight">{activeCase.title}</h2>
              <p className="text-xs text-white/70 mt-2">📍 {activeCase.location}</p>
            </div>

            {/* Modal Body Content */}
            <div className="p-8 sm:p-10 space-y-8 text-gray-700">
              
              {/* Highlight Metrics */}
              <div className="grid grid-cols-3 gap-6 bg-gradient-to-br from-gray-50 to-white border border-gray-100 p-6 rounded-2xl text-center">
                {activeCase.metrics.map((metric, i) => (
                  <div key={i}>
                    <div className="text-2xl sm:text-3xl font-black text-gold">{metric.value}</div>
                    <div className="text-xs sm:text-sm text-gray-500 font-semibold mt-1">{metric.label}</div>
                  </div>
                ))}
              </div>

              {/* Challenge Section */}
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-black mb-3 flex items-center gap-2">
                  <span className="text-red-500 text-lg">⚠️</span> The Challenge
                </h3>
                <p className="leading-relaxed text-sm sm:text-base">{activeCase.challenge}</p>
              </div>

              {/* Solution Section */}
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-black mb-3 flex items-center gap-2">
                  <span className="text-gold text-xl">💡</span> The Solution
                </h3>
                <p className="leading-relaxed text-sm sm:text-base">{activeCase.solution}</p>
              </div>

              {/* Results Checklist */}
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-black mb-3 flex items-center gap-2">
                  <span className="text-emerald-500 text-xl">📈</span> Key Results & Impact
                </h3>
                <ul className="space-y-2 text-sm sm:text-base">
                  {activeCase.results.map((result, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="text-emerald-500 font-bold mt-0.5">✓</span>
                      <span>{result}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Technology Stack Grid */}
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-black mb-3 flex items-center gap-2">
                  <span className="text-blue-500 text-xl">🛠️</span> Tech Stack
                </h3>
                <div className="flex flex-wrap gap-2">
                  {activeCase.techStack.map((tech) => (
                    <span 
                      key={tech} 
                      className="bg-gray-100 text-gray-800 text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-lg border border-gray-200"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              {/* CTA contact */}
              <div className="bg-gray-950 text-white p-8 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-6 border border-gold/30 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-gold/5 to-transparent"></div>
                <div className="relative z-10 text-center md:text-left">
                  <h4 className="text-gold font-bold text-xl mb-1">Have similar challenges?</h4>
                  <p className="text-gray-300 text-sm">Let\'s configure an automated AI solution for your workflows.</p>
                </div>
                <a 
                  href="/contact"
                  onClick={() => setActiveCase(null)}
                  className="bg-gold text-black font-bold px-6 py-3 rounded-xl hover:bg-gold-dark transition-all transform hover:-translate-y-0.5 text-center whitespace-nowrap relative z-10"
                >
                  Consult an Expert
                </a>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseStudiesPage;
